import { useEffect, useState } from 'react';
import FarmsEditor from './FarmsEditor';
import KcTable from './KcTable';

// Campaign types mirror functions/_lib/campaigns.ts. Duplicated rather than
// shared because the admin is a separate bundle path and we don't have a
// shared types package.
const ALL_BLOCKS = [
  'alerts',
  'frost',
  'today',
  'tomorrow',
  'sun',
  'moon',
  'air',
  'playable',
  'olive',
  'irrigation',
  'astro',
  'planets',
] as const;
type BlockKey = (typeof ALL_BLOCKS)[number];

const ALL_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type DayKey = (typeof ALL_DAYS)[number];

interface Recipient {
  email: string;
  name?: string;
  overrideBlocks?: BlockKey[];
}

interface Campaign {
  id: string;
  name: string;
  enabled: boolean;
  hour: number;
  minute: number;
  timezone: string;
  days: DayKey[];
  recipients: Recipient[];
  blocks: BlockKey[];
  subjectTemplate?: string;
  lastRunAt?: string;
}

type Tab = 'campaigns' | 'farms' | 'kc';
const TABS: Tab[] = ['campaigns', 'farms', 'kc'];

// Cross-links from the dashboard land on /admin?tab=farms&farm=foliage; keep the
// tab in the URL so reload and back/forward stay on the same editor.
function readUrlState(): { tab: Tab; farm: string | null } {
  const params = new URLSearchParams(window.location.search);
  const t = params.get('tab');
  return {
    tab: TABS.includes(t as Tab) ? (t as Tab) : 'campaigns',
    farm: params.get('farm'),
  };
}

export default function AdminApp() {
  const [authed, setAuthed] = useState(false);
  const [urlState] = useState(readUrlState);
  const [tab, setTab] = useState<Tab>(urlState.tab);
  const backHref = urlState.farm ? `/?farm=${encodeURIComponent(urlState.farm)}` : '/';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab);
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, [tab]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch('/api/digest/campaigns');
      if (r.status === 403) {
        setError('Not signed in — reload the page to sign in with Cloudflare Access.');
        setAuthed(false);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = (await r.json()) as { campaigns?: Campaign[] };
      setCampaigns(body.campaigns ?? []);
      setAuthed(true);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const r = await fetch('/api/digest/campaigns', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaigns }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? `HTTP ${r.status}`);
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  async function testSend(campaignId: string) {
    try {
      // /api/admin/* is behind Cloudflare Access; /api/digest/send is not
      // (cron-job.org calls it with a Bearer secret), so the browser uses
      // this alias to carry its login identity.
      const r = await fetch(`/api/admin/digest-send?campaign=${encodeURIComponent(campaignId)}`, {
        method: 'POST',
      });
      // Cloudflare returns HTML on Worker crash. Guard JSON parse.
      const text = await r.text();
      let j: {
        ok?: boolean;
        error?: string;
        subject?: string;
        recipients?: Array<{ email: string; ok: boolean; error?: string }>;
      };
      try {
        j = JSON.parse(text);
      } catch {
        throw new Error(`Server returned non-JSON (HTTP ${r.status}). First 200 chars: ${text.slice(0, 200)}`);
      }
      if (Array.isArray(j.recipients)) {
        const ok = j.recipients.filter((p) => p.ok).length;
        const failed = j.recipients.filter((p) => !p.ok);
        if (failed.length === 0) {
          alert(`Sent to ${ok} recipient(s).\nSubject: ${j.subject}`);
        } else {
          const detail = failed.map((p) => `• ${p.email}: ${p.error}`).join('\n');
          alert(
            `${ok} sent, ${failed.length} failed.\n\nFailures:\n${detail}\n\nSubject: ${j.subject}`,
          );
        }
        return;
      }
      if (!r.ok) throw new Error(j.error ?? `HTTP ${r.status}`);
      alert(`Sent: ${j.subject}`);
    } catch (err) {
      alert(`Test send failed: ${err}`);
    }
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
        <div className="bg-slate-900 rounded-xl p-6 max-w-sm w-full border border-slate-800 text-center">
          <h1 className="text-lg font-medium mb-4">Admin access</h1>
          <div className="text-sm opacity-80">
            {loading
              ? 'Checking sign-in…'
              : error ?? 'Not signed in — reload the page to sign in with Cloudflare Access.'}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 w-full bg-sky-600 hover:bg-sky-500 rounded px-3 py-2 text-sm font-medium"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 sm:p-6">
      <header className="max-w-4xl mx-auto flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-light">
            {tab === 'campaigns' ? 'Digest campaigns' : tab === 'farms' ? 'Farms & blocks' : 'Kc table'}
          </h1>
          {tab === 'campaigns' && (
            <div className="text-xs opacity-60">
              {campaigns.length} campaign{campaigns.length === 1 ? '' : 's'} · cron-job.org should call /api/digest/tick every 15 min
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {tab === 'campaigns' && (
            <>
              <button
                onClick={() =>
                  setCampaigns((cs) => [
                    ...cs,
                    {
                      id: crypto.randomUUID(),
                      name: 'New campaign',
                      enabled: false,
                      hour: 5,
                      minute: 0,
                      timezone: 'America/Los_Angeles',
                      days: [...ALL_DAYS],
                      recipients: [],
                      blocks: ['alerts', 'frost', 'today', 'tomorrow', 'sun', 'air'],
                    },
                  ])
                }
                className="bg-emerald-600 hover:bg-emerald-500 rounded px-3 py-1.5 text-sm"
              >
                + Add campaign
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="bg-sky-600 hover:bg-sky-500 rounded px-3 py-1.5 text-sm disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save all'}
              </button>
            </>
          )}
          <a href={backHref} className="text-xs opacity-60 hover:opacity-90 ml-2">
            ← back to dashboard
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto flex items-center gap-2 mb-4">
        <button
          onClick={() => setTab('campaigns')}
          className={`text-sm px-3 py-1.5 rounded ${tab === 'campaigns' ? 'bg-emerald-600' : 'bg-slate-800'}`}
        >
          Digest campaigns
        </button>
        <button
          onClick={() => setTab('farms')}
          className={`text-sm px-3 py-1.5 rounded ${tab === 'farms' ? 'bg-emerald-600' : 'bg-slate-800'}`}
        >
          Farms & blocks
        </button>
        <button
          onClick={() => setTab('kc')}
          className={`text-sm px-3 py-1.5 rounded ${tab === 'kc' ? 'bg-emerald-600' : 'bg-slate-800'}`}
        >
          Kc table
        </button>
      </div>

      {error && (
        <div className="max-w-4xl mx-auto mb-4 p-3 rounded bg-rose-900/50 border border-rose-500/40 text-sm">
          {error}
        </div>
      )}

      {tab === 'campaigns' && (
        <div className="max-w-4xl mx-auto space-y-3">
          {campaigns.map((c, idx) => (
            <CampaignEditor
              key={c.id}
              campaign={c}
              onChange={(next) =>
                setCampaigns((cs) => cs.map((x, i) => (i === idx ? next : x)))
              }
              onDelete={() => setCampaigns((cs) => cs.filter((_, i) => i !== idx))}
              onTest={() => testSend(c.id)}
            />
          ))}
          {campaigns.length === 0 && (
            <div className="text-center opacity-60 py-12 text-sm">
              No campaigns yet. Click "+ Add campaign" to create one.
            </div>
          )}
        </div>
      )}

      {tab === 'farms' && (
        <div className="max-w-4xl mx-auto">
          <FarmsEditor initialFarmId={urlState.farm} />
        </div>
      )}

      {tab === 'kc' && (
        <div className="max-w-4xl mx-auto">
          <KcTable />
        </div>
      )}
    </div>
  );
}

function CampaignEditor({
  campaign,
  onChange,
  onDelete,
  onTest,
}: {
  campaign: Campaign;
  onChange: (c: Campaign) => void;
  onDelete: () => void;
  onTest: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg">
      <div className="flex items-center gap-3 p-3">
        <input
          type="checkbox"
          checked={campaign.enabled}
          onChange={(e) => onChange({ ...campaign, enabled: e.target.checked })}
          className="w-4 h-4"
        />
        <input
          value={campaign.name}
          onChange={(e) => onChange({ ...campaign, name: e.target.value })}
          className="flex-1 bg-transparent text-sm font-medium focus:outline-none"
        />
        <div className="text-xs opacity-70 tabular-nums">
          {String(campaign.hour).padStart(2, '0')}:{String(campaign.minute).padStart(2, '0')}
          <span className="opacity-60 ml-1">{campaign.days.join(',')}</span>
        </div>
        <div className="text-xs opacity-60">
          {campaign.recipients.length} recipient{campaign.recipients.length === 1 ? '' : 's'}
        </div>
        <button onClick={() => setOpen(!open)} className="text-xs px-2 py-1 bg-slate-800 rounded">
          {open ? 'Close' : 'Edit'}
        </button>
        <button onClick={onTest} className="text-xs px-2 py-1 bg-emerald-700 rounded">
          Test send
        </button>
        <button onClick={onDelete} className="text-xs px-2 py-1 bg-rose-800 rounded">
          Delete
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-800 p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Hour (0-23, local)</div>
              <input
                type="number"
                min={0}
                max={23}
                value={campaign.hour}
                onChange={(e) => onChange({ ...campaign, hour: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Minute (0 or 15/30/45)</div>
              <input
                type="number"
                min={0}
                max={59}
                step={15}
                value={campaign.minute}
                onChange={(e) => onChange({ ...campaign, minute: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Timezone</div>
              <input
                value={campaign.timezone}
                onChange={(e) => onChange({ ...campaign, timezone: e.target.value })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
          </div>

          <div>
            <div className="text-xs opacity-60 mb-1">Days</div>
            <div className="flex gap-1 flex-wrap">
              {ALL_DAYS.map((d) => (
                <button
                  key={d}
                  onClick={() =>
                    onChange({
                      ...campaign,
                      days: campaign.days.includes(d)
                        ? campaign.days.filter((x) => x !== d)
                        : [...campaign.days, d],
                    })
                  }
                  className={`text-xs px-3 py-1 rounded ${
                    campaign.days.includes(d) ? 'bg-sky-600' : 'bg-slate-800'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs opacity-60 mb-1">Campaign blocks (default for all recipients)</div>
            <BlockPicker
              value={campaign.blocks}
              onChange={(blocks) => onChange({ ...campaign, blocks })}
            />
          </div>

          <label className="block">
            <div className="text-xs opacity-60 mb-1">Subject template (optional)</div>
            <input
              placeholder="{location} — {hi}/{lo}, {condition}"
              value={campaign.subjectTemplate ?? ''}
              onChange={(e) =>
                onChange({ ...campaign, subjectTemplate: e.target.value || undefined })
              }
              className="w-full bg-slate-800 rounded px-2 py-1 text-sm"
            />
            <div className="text-[10px] opacity-50 mt-1">
              Tokens: {'{location}, {hi}, {lo}, {condition}'}
            </div>
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs opacity-60">Recipients</div>
              <button
                onClick={() =>
                  onChange({
                    ...campaign,
                    recipients: [...campaign.recipients, { email: '' }],
                  })
                }
                className="text-xs px-2 py-1 bg-slate-800 rounded"
              >
                + Add recipient
              </button>
            </div>
            <div className="space-y-2">
              {campaign.recipients.map((r, i) => (
                <RecipientRow
                  key={i}
                  recipient={r}
                  campaignBlocks={campaign.blocks}
                  onChange={(next) =>
                    onChange({
                      ...campaign,
                      recipients: campaign.recipients.map((x, j) => (j === i ? next : x)),
                    })
                  }
                  onDelete={() =>
                    onChange({
                      ...campaign,
                      recipients: campaign.recipients.filter((_, j) => j !== i),
                    })
                  }
                />
              ))}
            </div>
          </div>

          {campaign.lastRunAt && (
            <div className="text-[10px] opacity-50">
              Last run: {new Date(campaign.lastRunAt).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RecipientRow({
  recipient,
  campaignBlocks,
  onChange,
  onDelete,
}: {
  recipient: Recipient;
  campaignBlocks: BlockKey[];
  onChange: (r: Recipient) => void;
  onDelete: () => void;
}) {
  const [customize, setCustomize] = useState(!!recipient.overrideBlocks);
  return (
    <div className="border border-slate-800 rounded p-2">
      <div className="flex gap-2">
        <input
          placeholder="Name (optional)"
          value={recipient.name ?? ''}
          onChange={(e) => onChange({ ...recipient, name: e.target.value || undefined })}
          className="bg-slate-800 rounded px-2 py-1 text-sm w-40"
        />
        <input
          placeholder="email@example.com"
          value={recipient.email}
          onChange={(e) => onChange({ ...recipient, email: e.target.value })}
          className="flex-1 bg-slate-800 rounded px-2 py-1 text-sm"
        />
        <button onClick={onDelete} className="text-xs px-2 py-1 bg-rose-900 rounded">
          ×
        </button>
      </div>
      <label className="inline-flex items-center gap-2 mt-2 text-xs opacity-80">
        <input
          type="checkbox"
          checked={customize}
          onChange={(e) => {
            setCustomize(e.target.checked);
            onChange({
              ...recipient,
              overrideBlocks: e.target.checked ? [...campaignBlocks] : undefined,
            });
          }}
        />
        Customize blocks for this recipient
      </label>
      {customize && recipient.overrideBlocks && (
        <div className="mt-2">
          <BlockPicker
            value={recipient.overrideBlocks}
            onChange={(blocks) => onChange({ ...recipient, overrideBlocks: blocks })}
          />
        </div>
      )}
    </div>
  );
}

function BlockPicker({
  value,
  onChange,
}: {
  value: BlockKey[];
  onChange: (v: BlockKey[]) => void;
}) {
  return (
    <div className="flex gap-1 flex-wrap">
      {ALL_BLOCKS.map((b) => (
        <button
          key={b}
          onClick={() =>
            onChange(value.includes(b) ? value.filter((x) => x !== b) : [...value, b])
          }
          className={`text-xs px-2.5 py-1 rounded ${
            value.includes(b) ? 'bg-emerald-600' : 'bg-slate-800'
          }`}
        >
          {b}
        </button>
      ))}
    </div>
  );
}
